import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  getTournamentMetaDecksStats, 
  getMetaDeckMatchups,
  MetaDeckStatItem, 
  MetaMatchupData 
} from '../../services/supabase/metaDecksService';
import { getCardgames } from '../../services/supabase/systemService';
import { getTournamentFormatsForReports } from '../../services/supabase/tournamentReportsService';
import { useAuth } from '../../components/AuthProvider';
import { GameSelector } from '../../components/GameSelector';

interface MetaProps {
  activeGame: number | 'All';
  setActiveGame: (game: number | 'All') => void;
}

// Metadados visuais de jogos para o seletor de jogos do Metagame
const GAME_VISUAL_CONFIG: Record<string, { icon: string; color: string; desc: string; badge: string }> = {
  '1': { icon: 'fa-anchor', color: 'from-red-600 to-amber-600', desc: 'Líderes, meta de OP-01 até o formato atual', badge: 'One Piece' },
  '2': { icon: 'fa-dragon', color: 'from-blue-600 to-cyan-600', desc: 'Formatos Standard e BT Sets', badge: 'Digimon' },
  '3': { icon: 'fa-bolt', color: 'from-yellow-500 to-amber-600', desc: 'Standard, Expanded e Campeonatos', badge: 'Pokémon' },
  '4': { icon: 'fa-wand-magic-sparkles', color: 'from-purple-600 to-orange-600', desc: 'Standard, Commander, Modern e Pioneer', badge: 'Magic' },
  '5': { icon: 'fa-eye', color: 'from-amber-600 to-purple-800', desc: 'Advanced, Speed Duel e Rush Duel', badge: 'Yu-Gi-Oh!' },
  '6': { icon: 'fa-fire', color: 'from-orange-600 to-red-600', desc: 'Fusion World e Masters', badge: 'Dragon Ball' },
  '7': { icon: 'fa-robot', color: 'from-blue-600 to-red-600', desc: 'Gundam Card Game Sets', badge: 'Gundam' },
  '8': { icon: 'fa-sparkles', color: 'from-indigo-600 to-amber-500', desc: 'Core e Constructed Formats', badge: 'Disney Lorcana' },
  '9': { icon: 'fa-layer-group', color: 'from-pink-600 to-purple-600', desc: 'Union Arena Anime Decks', badge: 'Union Arena' },
  '10': { icon: 'fa-jedi', color: 'from-yellow-500 to-slate-900', desc: 'Premier e Twin Suns Formats', badge: 'Star Wars' },
  '11': { icon: 'fa-shield-halved', color: 'from-sky-600 to-indigo-600', desc: 'Standard e Premium Formats', badge: 'Vanguard' },
  '12': { icon: 'fa-hand-fist', color: 'from-red-700 to-orange-700', desc: 'Classic Constructed e Blitz', badge: 'FaB' },
  '13': { icon: 'fa-hat-wizard', color: 'from-emerald-700 to-teal-900', desc: 'Contested Realm Constructed', badge: 'Sorcery' },
  '14': { icon: 'fa-gem', color: 'from-teal-600 to-purple-700', desc: 'Competitive e Classic Formats', badge: 'Riftbound' },
  '15': { icon: 'fa-burst', color: 'from-violet-600 to-rose-600', desc: 'Standard e Retro Formats', badge: 'UniVersus' }
};

export const Meta: React.FC<MetaProps> = ({ activeGame, setActiveGame }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Estados principais
  const [metaDecks, setMetaDecks] = useState<MetaDeckStatItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [dbGames, setDbGames] = useState<{ id: any; name: string; slug?: string }[]>([]);
  const [formats, setFormats] = useState<any[]>([]);
  const [selectedFormat, setSelectedFormat] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTierFilter, setSelectedTierFilter] = useState<string>('All');
  const [viewMode, setViewMode] = useState<'tiers' | 'table' | 'analytics'>('tiers');
  const [sortBy, setSortBy] = useState<'rounds' | 'reports' | 'name' | 'winrate'>('rounds');
  const [showGameSwitchDropdown, setShowGameSwitchDropdown] = useState(false);

  // Fechar dropdown de troca de jogo ao clicar fora
  useEffect(() => {
    const handleGlobalClick = () => {
      setShowGameSwitchDropdown(false);
    };
    window.addEventListener('click', handleGlobalClick);
    return () => window.removeEventListener('click', handleGlobalClick);
  }, []);

  // Modal de Detalhes do Meta Deck
  const [selectedDeckDetails, setSelectedDeckDetails] = useState<MetaDeckStatItem | null>(null);
  const [deckMatchups, setDeckMatchups] = useState<MetaMatchupData[]>([]);
  const [loadingMatchups, setLoadingMatchups] = useState(false);

  const isGameSelected = activeGame !== 'All' && activeGame !== 0 && Boolean(activeGame);

  // Nome do jogo ativo a partir do seletor global
  const currentGameName = useMemo(() => {
    if (!isGameSelected) return '';
    const found = dbGames.find(g => Number(g.id) === Number(activeGame));
    return found?.name || 'Jogo Selecionado';
  }, [activeGame, dbGames, isGameSelected]);

  // Carregar jogos disponíveis
  useEffect(() => {
    const fetchGames = async () => {
      try {
        const games = await getCardgames();
        setDbGames(games);
      } catch (err) {
        console.warn('Error loading games for meta:', err);
      }
    };
    fetchGames();
  }, []);

  // Carregar formatos para o jogo ativo
  useEffect(() => {
    if (!isGameSelected) {
      setFormats([]);
      return;
    }

    const fetchFormats = async () => {
      try {
        const fList = await getTournamentFormatsForReports(activeGame);
        setFormats(fList || []);
      } catch (err) {
        console.warn('Error loading formats:', err);
      }
    };
    fetchFormats();
  }, [activeGame, isGameSelected]);

  // Carregar estatísticas do meta (da view v_tournament_meta_decks_stats)
  const fetchStats = async () => {
    if (!isGameSelected) {
      setMetaDecks([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await getTournamentMetaDecksStats(activeGame, selectedFormat);
      setMetaDecks(data);
    } catch (err) {
      console.error('Error fetching meta decks stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [activeGame, selectedFormat, isGameSelected]);

  // Carregar confrontos quando um deck for selecionado
  const handleOpenDeckDetails = async (deck: MetaDeckStatItem) => {
    setSelectedDeckDetails(deck);
    setLoadingMatchups(true);
    try {
      const matchups = await getMetaDeckMatchups(deck.id);
      setDeckMatchups(matchups);
    } catch (err) {
      console.warn('Error fetching matchups for deck:', err);
      setDeckMatchups([]);
    } finally {
      setLoadingMatchups(false);
    }
  };

  // Filtragem e ordenação dos decks
  const filteredAndSortedDecks = useMemo(() => {
    let list = [...metaDecks];

    // Busca textual
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(d => {
        const matchName = d.name.toLowerCase().includes(q);
        const matchDisplay = (d.display_name || '').toLowerCase().includes(q);
        const matchCode = (d.code || '').toLowerCase().includes(q);
        const matchColor = (d.colors || []).some(c => c.toLowerCase().includes(q));
        return matchName || matchDisplay || matchCode || matchColor;
      });
    }

    // Filtro por Tier
    if (selectedTierFilter !== 'All') {
      list = list.filter(d => d.tier === selectedTierFilter);
    }

    // Ordenação
    list.sort((a, b) => {
      if (sortBy === 'rounds') return b.rounds_count - a.rounds_count || b.meta_share - a.meta_share;
      if (sortBy === 'reports') return b.reports_count - a.reports_count || b.rounds_count - a.rounds_count;
      if (sortBy === 'name') return a.name.localeCompare(b.name, 'pt-BR');
      if (sortBy === 'winrate') return (b.win_rate_against || 0) - (a.win_rate_against || 0);
      return 0;
    });

    return list;
  }, [metaDecks, searchQuery, selectedTierFilter, sortBy]);

  // Estatísticas agregadas globais
  const summaryStats = useMemo(() => {
    const totalRounds = metaDecks.reduce((acc, cur) => acc + (cur.rounds_count || 0), 0);
    const totalReports = metaDecks.reduce((acc, cur) => acc + (cur.reports_count || 0), 0);
    const topDeck = metaDecks.length > 0 ? metaDecks[0] : null;
    const tier1Count = metaDecks.filter(d => d.tier === 'Tier 1').length;
    const tier2Count = metaDecks.filter(d => d.tier === 'Tier 2').length;
    const activeArchetypes = metaDecks.filter(d => d.rounds_count > 0 || metaDecks.length <= 10).length;

    return {
      totalRounds,
      totalReports,
      topDeck,
      tier1Count,
      tier2Count,
      activeArchetypes: activeArchetypes || metaDecks.length
    };
  }, [metaDecks]);

  // Agrupamento por Tiers para visualização em seções
  const groupedByTier = useMemo(() => {
    return {
      tier1: filteredAndSortedDecks.filter(d => d.tier === 'Tier 1'),
      tier2: filteredAndSortedDecks.filter(d => d.tier === 'Tier 2'),
      tier3: filteredAndSortedDecks.filter(d => d.tier === 'Tier 3'),
      rogue: filteredAndSortedDecks.filter(d => d.tier === 'Rogue')
    };
  }, [filteredAndSortedDecks]);

  // Helper de cores dos decks
  const renderColorBadges = (colors?: string[] | null) => {
    if (!colors || colors.length === 0) return null;

    const getColorStyle = (c: string) => {
      const low = c.toLowerCase();
      if (low.includes('red') || low.includes('vermelho')) return 'bg-red-500/20 text-red-300 border-red-500/40';
      if (low.includes('blue') || low.includes('azul') || low.includes('sapphire')) return 'bg-blue-500/20 text-blue-300 border-blue-500/40';
      if (low.includes('green') || low.includes('verde') || low.includes('emerald')) return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40';
      if (low.includes('purple') || low.includes('roxo') || low.includes('amethyst')) return 'bg-purple-500/20 text-purple-300 border-purple-500/40';
      if (low.includes('yellow') || low.includes('amarelo') || low.includes('amber')) return 'bg-amber-500/20 text-amber-300 border-amber-500/40';
      if (low.includes('black') || low.includes('preto') || low.includes('steel')) return 'bg-slate-700/50 text-slate-300 border-slate-500/40';
      if (low.includes('ruby')) return 'bg-rose-500/20 text-rose-300 border-rose-500/40';
      if (low.includes('white') || low.includes('branco')) return 'bg-slate-200/20 text-slate-100 border-slate-300/40';
      return 'bg-purple-900/30 text-purple-200 border-purple-700/40';
    };

    return (
      <div className="flex flex-wrap gap-1">
        {colors.map((c, idx) => (
          <span 
            key={`${c}-${idx}`}
            className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${getColorStyle(c)}`}
          >
            {c}
          </span>
        ))}
      </div>
    );
  };

  // Helper de Badge de Tier
  const renderTierBadge = (tier: string) => {
    switch (tier) {
      case 'Tier 1':
        return (
          <span className="px-2.5 py-1 rounded-lg bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/40 text-amber-300 text-[10px] font-black uppercase tracking-wider flex items-center space-x-1 shadow-sm shadow-amber-500/10">
            <i className="fas fa-crown text-[9px]"></i>
            <span>Tier 1</span>
          </span>
        );
      case 'Tier 2':
        return (
          <span className="px-2.5 py-1 rounded-lg bg-blue-500/20 border border-blue-500/40 text-blue-300 text-[10px] font-black uppercase tracking-wider flex items-center space-x-1">
            <i className="fas fa-medal text-[9px]"></i>
            <span>Tier 2</span>
          </span>
        );
      case 'Tier 3':
        return (
          <span className="px-2.5 py-1 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[10px] font-black uppercase tracking-wider flex items-center space-x-1">
            <i className="fas fa-shield-halved text-[9px]"></i>
            <span>Tier 3</span>
          </span>
        );
      default:
        return (
          <span className="px-2.5 py-1 rounded-lg bg-slate-800 border border-slate-700 text-slate-400 text-[10px] font-black uppercase tracking-wider flex items-center space-x-1">
            <i className="fas fa-dice text-[9px]"></i>
            <span>Rogue</span>
          </span>
        );
    }
  };

  // Se nenhum jogo estiver selecionado (activeGame === 'All' ou 0), exibe o seletor padronizado
  if (!isGameSelected) {
    return (
      <div className="min-h-[75vh] flex items-center justify-center">
        <GameSelector
          games={dbGames}
          onSelectGame={(id) => setActiveGame(id as number)}
          title="Escolha um Jogo"
          subtitle="Para analisar o metagame competitivo, tiers e arquétipos dominantes, selecione uma franquia de TCG:"
        />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6 animate-in fade-in duration-500 px-2 sm:px-4 pb-16">
      {/* Top Banner / Hero */}
      <div className="space-y-4 sm:space-y-6">
        <div className="flex items-start sm:items-center justify-between gap-3 sm:gap-4 border-b border-slate-800/80 pb-4 sm:pb-6">
          <div className="flex items-start sm:items-center space-x-2.5 sm:space-x-3 min-w-0 flex-1">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-pink-400 flex items-center justify-center shadow-md shadow-purple-600/20 border border-purple-400/20 shrink-0">
              <i className="fas fa-ranking-star text-white text-base sm:text-lg"></i>
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-black text-white tracking-tight">
                Metagame
              </h1>
              <p className="text-xs sm:text-sm text-slate-400 mt-0.5 line-clamp-2">
                {isGameSelected 
                  ? `Análise de desempenho e presença de decks em ${currentGameName}`
                  : 'Estatísticas competitivas e arquétipos dominantes por Card Game'
                }
              </p>
            </div>
          </div>

          {/* Game Selector Badges & Quick Action */}
          {isGameSelected && (
            <div className="flex items-center shrink-0 pt-0.5 sm:pt-0">
              <button
                onClick={() => setActiveGame('All')}
                className="flex items-center space-x-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-300 hover:text-white text-xs font-bold transition-all cursor-pointer shadow-sm active:scale-95 whitespace-nowrap"
                title="Voltar para a seleção de todos os jogos"
              >
                <i className="fa-solid fa-shapes text-[10px] text-purple-400"></i>
                <span>Trocar Jogo</span>
              </button>
            </div>
          )}
        </div>

          {/* Cards de Métricas Rápidas (Apenas quando um jogo estiver selecionado) */}
          {isGameSelected && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4 pt-1 sm:pt-2">
              {/* Top Deck Dominante */}
              <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-3 sm:p-4 relative overflow-hidden shadow-sm">
                <div className="flex items-center justify-between text-slate-400 mb-1.5 sm:mb-2">
                  <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-slate-400">Deck Dominante</span>
                  <i className="fas fa-crown text-amber-400 text-xs sm:text-sm"></i>
                </div>
                <div className="flex items-center space-x-2.5 sm:space-x-3 min-w-0">
                  {summaryStats.topDeck?.image_url ? (
                    <img 
                      src={summaryStats.topDeck.image_url} 
                      alt="" 
                      className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg object-cover border border-slate-700 flex-shrink-0" 
                    />
                  ) : (
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center text-amber-400 flex-shrink-0">
                      <i className="fas fa-shield text-xs sm:text-sm"></i>
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <h4 className="text-xs sm:text-sm font-black text-white truncate">
                      {summaryStats.topDeck?.display_name || summaryStats.topDeck?.name || 'Aguardando dados'}
                    </h4>
                    <p className="text-[11px] sm:text-xs text-amber-400 font-bold font-mono tabular-nums">
                      {summaryStats.topDeck?.meta_share || 0}% de presença
                    </p>
                  </div>
                </div>
              </div>

              {/* Total de Rodadas Analisadas */}
              <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-3 sm:p-4 shadow-sm">
                <div className="flex items-center justify-between text-slate-400 mb-1.5 sm:mb-2">
                  <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-slate-400">Rodadas</span>
                  <i className="fas fa-bolt text-purple-400 text-xs sm:text-sm"></i>
                </div>
                <div className="text-xl sm:text-2xl font-black text-white font-mono tabular-nums">
                  {summaryStats.totalRounds}
                </div>
                <p className="text-[10px] sm:text-[11px] text-slate-400 font-medium mt-0.5 truncate">
                  Confrontos em torneios
                </p>
              </div>

              {/* Torneios com Reports */}
              <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-3 sm:p-4 shadow-sm">
                <div className="flex items-center justify-between text-slate-400 mb-1.5 sm:mb-2">
                  <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-slate-400">Reports</span>
                  <i className="fas fa-clipboard-check text-blue-400 text-xs sm:text-sm"></i>
                </div>
                <div className="text-xl sm:text-2xl font-black text-white font-mono tabular-nums">
                  {summaryStats.totalReports}
                </div>
                <p className="text-[10px] sm:text-[11px] text-slate-400 font-medium mt-0.5 truncate">
                  Torneios reportados
                </p>
              </div>

              {/* Arquétipos no Meta */}
              <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-3 sm:p-4 shadow-sm">
                <div className="flex items-center justify-between text-slate-400 mb-1.5 sm:mb-2">
                  <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-slate-400">Arquétipos</span>
                  <i className="fas fa-layer-group text-emerald-400 text-xs sm:text-sm"></i>
                </div>
                <div className="text-xl sm:text-2xl font-black text-white font-mono tabular-nums">
                  {summaryStats.activeArchetypes}
                </div>
                <p className="text-[10px] sm:text-[11px] text-slate-400 font-medium mt-0.5 truncate">
                  {summaryStats.tier1Count} T1 • {summaryStats.tier2Count} T2
                </p>
              </div>
            </div>
          )}
      </div>

      {/* Main Content Area */}
      <div className="space-y-4 sm:space-y-6">

        {/* Filters and View Controls Bar */}
        <div className="bg-slate-900/90 border border-slate-800 p-3.5 sm:p-4 rounded-2xl space-y-3 shadow-sm">
          {/* Search Input (ocupa uma linha sozinho) */}
          <div className="relative w-full">
            <i className="fas fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs pointer-events-none"></i>
            <input
              type="text"
              placeholder={`Buscar arquétipo em ${currentGameName}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-9 pr-8 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 transition-colors"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-xs p-1 cursor-pointer"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>

          {/* Tier & Sort Filters & View Mode */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2.5 sm:gap-3">
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 w-full md:w-auto">
              {/* Formato */}
              {formats.length > 0 && (
                <div className="relative w-full sm:w-auto">
                  <select
                    value={selectedFormat}
                    onChange={(e) => setSelectedFormat(e.target.value)}
                    aria-label="Filtrar por Formato"
                    className="w-full sm:w-auto bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-300 focus:outline-none focus:border-purple-500 font-bold appearance-none pr-8 cursor-pointer truncate"
                  >
                    <option value="All">Todos os Formatos</option>
                    {formats.map(fmt => (
                      <option key={fmt.id} value={fmt.id}>{fmt.name}</option>
                    ))}
                  </select>
                  <i className="fas fa-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 pointer-events-none"></i>
                </div>
              )}

              {/* Tier */}
              <div className="relative w-full sm:w-auto">
                <select
                  value={selectedTierFilter}
                  onChange={(e) => setSelectedTierFilter(e.target.value)}
                  aria-label="Filtrar por Tier"
                  className="w-full sm:w-auto bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-300 focus:outline-none focus:border-purple-500 font-bold appearance-none pr-8 cursor-pointer truncate"
                >
                  <option value="All">Todos os Tiers</option>
                  <option value="Tier 1">Tier 1</option>
                  <option value="Tier 2">Tier 2</option>
                  <option value="Tier 3">Tier 3</option>
                  <option value="Rogue">Rogue</option>
                </select>
                <i className="fas fa-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 pointer-events-none"></i>
              </div>

              {/* Ordenação */}
              <div className="relative w-full sm:w-auto">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  aria-label="Ordenar por"
                  className="w-full sm:w-auto bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-300 focus:outline-none focus:border-purple-500 font-bold appearance-none pr-8 cursor-pointer truncate"
                >
                  <option value="rounds">Mais Enfrentados (Rodadas)</option>
                  <option value="reports">Mais Frequentes (Torneios)</option>
                  <option value="name">Nome (A - Z)</option>
                  <option value="winrate">Taxa de Vitória vs</option>
                </select>
                <i className="fas fa-sort absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 pointer-events-none"></i>
              </div>
            </div>

            {/* View Mode Toggle Buttons */}
            <div className="flex items-center justify-between sm:justify-start bg-slate-950 border border-slate-800 rounded-xl p-0.5 shrink-0 w-full md:w-auto">
              <button
                onClick={() => setViewMode('tiers')}
                className={`flex-1 md:flex-initial px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center space-x-1.5 cursor-pointer ${
                  viewMode === 'tiers'
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-white'
                }`}
                title="Visualização em Tier List"
              >
                <i className="fas fa-layer-group text-[11px]"></i>
                <span className="text-[11px]">Tier List</span>
              </button>

              <button
                onClick={() => setViewMode('table')}
                className={`flex-1 md:flex-initial px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center space-x-1.5 cursor-pointer ${
                  viewMode === 'table'
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-white'
                }`}
                title="Visualização em Tabela Detalhada"
              >
                <i className="fas fa-table-list text-[11px]"></i>
                <span className="text-[11px]">Tabela</span>
              </button>

              <button
                onClick={() => setViewMode('analytics')}
                className={`flex-1 md:flex-initial px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center space-x-1.5 cursor-pointer ${
                  viewMode === 'analytics'
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-white'
                }`}
                title="Gráficos e Estatísticas"
              >
                <i className="fas fa-chart-pie text-[11px]"></i>
                <span className="text-[11px]">Análise</span>
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="py-20 flex flex-col items-center justify-center space-y-4">
            <div className="w-12 h-12 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin"></div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Carregando dados do Metagame...
            </p>
          </div>
        ) : filteredAndSortedDecks.length === 0 ? (
          /* Empty State */
          <div className="py-16 text-center bg-slate-900/40 border border-dashed border-slate-800 rounded-2xl p-8">
            <div className="w-16 h-16 rounded-2xl bg-slate-800/80 flex items-center justify-center text-slate-500 mx-auto mb-4 text-2xl">
              <i className="fas fa-ranking-star"></i>
            </div>
            <h3 className="text-lg font-bold text-white mb-1">Nenhum arquétipo encontrado</h3>
            <p className="text-xs text-slate-400 max-w-md mx-auto mb-6">
              Nenhum deck meta corresponde aos filtros selecionados. Experimente alterar a busca ou o jogo selecionado.
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedTierFilter('All');
                setSelectedFormat('All');
              }}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs rounded-xl transition-colors"
            >
              Limpar Filtros
            </button>
          </div>
        ) : (
          <>
            {/* VIEW 1: TIER LIST VIEW */}
            {viewMode === 'tiers' && (
              <div className="space-y-6 sm:space-y-8">
                {/* Seção Tier 1 */}
                {groupedByTier.tier1.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2.5 sm:space-x-3">
                      <div className="px-2.5 sm:px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-slate-950 font-black text-[11px] sm:text-xs uppercase tracking-widest rounded-lg flex items-center space-x-1.5 shadow-sm shadow-amber-500/20">
                        <i className="fas fa-crown text-[10px]"></i>
                        <span>Tier 1 • Decks Dominantes</span>
                      </div>
                      <span className="text-xs text-slate-400 font-medium">
                        {groupedByTier.tier1.length} {groupedByTier.tier1.length === 1 ? 'arquétipo' : 'arquétipos'}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                      {groupedByTier.tier1.map((deck) => (
                        <div
                          key={deck.id}
                          onClick={() => handleOpenDeckDetails(deck)}
                          className="bg-slate-900 border border-amber-500/30 hover:border-amber-500/60 rounded-2xl p-3.5 sm:p-4 transition-all duration-150 cursor-pointer group shadow-sm active:scale-[0.99]"
                        >
                          <div className="flex items-start space-x-3 sm:space-x-3.5">
                            {/* Imagem do Líder / Carta */}
                            <div className="w-14 h-18 sm:w-16 sm:h-22 rounded-xl bg-slate-950 border border-slate-800 overflow-hidden shrink-0 flex items-center justify-center shadow-sm">
                              {deck.image_url ? (
                                <img
                                  src={deck.image_url}
                                  alt={deck.name}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center text-slate-600 bg-slate-950">
                                  <i className="fas fa-shield text-base mb-1 text-slate-500"></i>
                                  <span className="text-[8px] font-bold">TCG</span>
                                </div>
                              )}
                            </div>

                            {/* Informações */}
                            <div className="flex-1 min-w-0 space-y-1.5">
                              <div className="flex items-center justify-between gap-1">
                                {renderTierBadge(deck.tier)}
                                <span className="text-xs font-black text-amber-400 font-mono tabular-nums">
                                  {deck.meta_share}% Meta
                                </span>
                              </div>

                              <div>
                                <h3 className="text-sm font-black text-white group-hover:text-amber-300 transition-colors truncate">
                                  {deck.display_name || deck.name}
                                </h3>
                              </div>

                              {renderColorBadges(deck.colors)}

                              {/* Barra de Progresso de Meta Share */}
                              <div className="pt-1 space-y-1">
                                <div className="w-full bg-slate-950 rounded-full h-1.5 overflow-hidden border border-slate-800">
                                  <div
                                    className="bg-gradient-to-r from-amber-500 to-orange-500 h-full rounded-full"
                                    style={{ width: `${Math.min(100, Math.max(5, deck.meta_share))}%` }}
                                  ></div>
                                </div>
                                <div className="flex items-center justify-between text-[10px] text-slate-400 font-medium font-mono tabular-nums">
                                  <span>{deck.rounds_count} rodadas</span>
                                  <span>{deck.reports_count} torneios</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Seção Tier 2 */}
                {groupedByTier.tier2.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2.5 sm:space-x-3">
                      <div className="px-2.5 sm:px-3 py-1 bg-blue-600 text-white font-black text-[10px] sm:text-xs uppercase tracking-widest rounded-lg flex items-center space-x-1.5 shadow-sm shadow-blue-600/20">
                        <i className="fas fa-medal text-[10px]"></i>
                        <span>Tier 2 • Competitivos Fortes</span>
                      </div>
                      <span className="text-xs text-slate-400 font-medium">
                        {groupedByTier.tier2.length} {groupedByTier.tier2.length === 1 ? 'arquétipo' : 'arquétipos'}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                      {groupedByTier.tier2.map((deck) => (
                        <div
                          key={deck.id}
                          onClick={() => handleOpenDeckDetails(deck)}
                          className="bg-slate-900 border border-blue-500/20 hover:border-blue-500/50 rounded-2xl p-3.5 sm:p-4 transition-all duration-150 cursor-pointer group shadow-sm active:scale-[0.99]"
                        >
                          <div className="flex items-start space-x-3 sm:space-x-3.5">
                            <div className="w-14 h-18 sm:w-16 sm:h-22 rounded-xl bg-slate-950 border border-slate-800 overflow-hidden shrink-0 flex items-center justify-center shadow-sm">
                              {deck.image_url ? (
                                <img
                                  src={deck.image_url}
                                  alt={deck.name}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center text-slate-600 bg-slate-950">
                                  <i className="fas fa-shield text-base mb-1 text-slate-500"></i>
                                  <span className="text-[8px] font-bold">TCG</span>
                                </div>
                              )}
                            </div>

                            <div className="flex-1 min-w-0 space-y-1.5">
                              <div className="flex items-center justify-between gap-1">
                                {renderTierBadge(deck.tier)}
                                <span className="text-xs font-black text-blue-400 font-mono tabular-nums">
                                  {deck.meta_share}% Meta
                                </span>
                              </div>

                              <div>
                                <h3 className="text-sm font-black text-white group-hover:text-blue-300 transition-colors truncate">
                                  {deck.display_name || deck.name}
                                </h3>
                              </div>

                              {renderColorBadges(deck.colors)}

                              <div className="pt-1 space-y-1">
                                <div className="w-full bg-slate-950 rounded-full h-1.5 overflow-hidden border border-slate-800">
                                  <div
                                    className="bg-blue-500 h-full rounded-full"
                                    style={{ width: `${Math.min(100, Math.max(5, deck.meta_share))}%` }}
                                  ></div>
                                </div>
                                <div className="flex items-center justify-between text-[10px] text-slate-400 font-medium font-mono tabular-nums">
                                  <span>{deck.rounds_count} rodadas</span>
                                  <span>{deck.reports_count} torneios</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Seção Tier 3 */}
                {groupedByTier.tier3.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2.5 sm:space-x-3">
                      <div className="px-2.5 sm:px-3 py-1 bg-emerald-700 text-white font-black text-[10px] sm:text-xs uppercase tracking-widest rounded-lg flex items-center space-x-1.5 shadow-sm shadow-emerald-700/20">
                        <i className="fas fa-shield-halved text-[10px]"></i>
                        <span>Tier 3 • Concorrentes Viáveis</span>
                      </div>
                      <span className="text-xs text-slate-400 font-medium">
                        {groupedByTier.tier3.length} {groupedByTier.tier3.length === 1 ? 'arquétipo' : 'arquétipos'}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                      {groupedByTier.tier3.map((deck) => (
                        <div
                          key={deck.id}
                          onClick={() => handleOpenDeckDetails(deck)}
                          className="bg-slate-900 border border-slate-800 hover:border-emerald-500/40 rounded-2xl p-3.5 sm:p-4 transition-all duration-150 cursor-pointer group shadow-sm active:scale-[0.99]"
                        >
                          <div className="flex items-start space-x-3 sm:space-x-3.5">
                            <div className="w-14 h-18 sm:w-16 sm:h-22 rounded-xl bg-slate-950 border border-slate-800 overflow-hidden shrink-0 flex items-center justify-center shadow-sm">
                              {deck.image_url ? (
                                <img
                                  src={deck.image_url}
                                  alt={deck.name}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center text-slate-600 bg-slate-950">
                                  <i className="fas fa-shield text-base mb-1 text-slate-500"></i>
                                  <span className="text-[8px] font-bold">TCG</span>
                                </div>
                              )}
                            </div>

                            <div className="flex-1 min-w-0 space-y-1.5">
                              <div className="flex items-center justify-between gap-1">
                                {renderTierBadge(deck.tier)}
                                <span className="text-xs font-bold text-emerald-400 font-mono tabular-nums">
                                  {deck.meta_share}% Meta
                                </span>
                              </div>

                              <div>
                                <h3 className="text-sm font-bold text-white group-hover:text-emerald-300 transition-colors truncate">
                                  {deck.display_name || deck.name}
                                </h3>
                              </div>

                              {renderColorBadges(deck.colors)}

                              <div className="pt-1 space-y-1">
                                <div className="w-full bg-slate-950 rounded-full h-1.5 overflow-hidden border border-slate-800">
                                  <div
                                    className="bg-emerald-500 h-full rounded-full"
                                    style={{ width: `${Math.min(100, Math.max(5, deck.meta_share))}%` }}
                                  ></div>
                                </div>
                                <div className="flex items-center justify-between text-[10px] text-slate-400 font-medium font-mono tabular-nums">
                                  <span>{deck.rounds_count} rodadas</span>
                                  <span>{deck.reports_count} torneios</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Seção Rogue / Outros */}
                {groupedByTier.rogue.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2.5 sm:space-x-3">
                      <div className="px-2.5 sm:px-3 py-1 bg-slate-800 text-slate-300 font-black text-[11px] sm:text-xs uppercase tracking-widest rounded-lg flex items-center space-x-1.5">
                        <i className="fas fa-dice text-[10px]"></i>
                        <span>Rogue</span>
                      </div>
                      <span className="text-xs text-slate-400 font-medium">
                        {groupedByTier.rogue.length} {groupedByTier.rogue.length === 1 ? 'arquétipo' : 'arquétipos'}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                      {groupedByTier.rogue.map((deck) => (
                        <div
                          key={deck.id}
                          onClick={() => handleOpenDeckDetails(deck)}
                          className="bg-slate-900 border border-slate-800 hover:border-slate-700 rounded-2xl p-3.5 sm:p-4 transition-all duration-150 cursor-pointer group active:scale-[0.99]"
                        >
                          <div className="flex items-start space-x-3 sm:space-x-3.5">
                            <div className="w-14 h-18 sm:w-16 sm:h-22 rounded-xl bg-slate-950 border border-slate-800 overflow-hidden shrink-0 flex items-center justify-center shadow-sm">
                              {deck.image_url ? (
                                <img
                                  src={deck.image_url}
                                  alt={deck.name}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center text-slate-600 bg-slate-950">
                                  <i className="fas fa-shield text-base text-slate-500"></i>
                                </div>
                              )}
                            </div>

                            <div className="flex-1 min-w-0 space-y-1">
                              <div className="flex items-center justify-between gap-1">
                                {renderTierBadge(deck.tier)}
                                <span className="text-xs font-bold text-slate-400 font-mono tabular-nums">
                                  {deck.meta_share}% Meta
                                </span>
                              </div>

                              <h3 className="text-sm font-bold text-white group-hover:text-purple-300 transition-colors truncate">
                                {deck.display_name || deck.name}
                              </h3>

                              {renderColorBadges(deck.colors)}

                              <div className="flex items-center justify-between text-[10px] text-slate-400 pt-1 font-mono tabular-nums">
                                <span>{deck.rounds_count} rodadas</span>
                                <span>{deck.reports_count} torneios</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* VIEW 2: TABLE VIEW */}
            {viewMode === 'table' && (
              <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-sm">
                {/* Mobile View (< sm): Clean Compact Card List (Zero Horizontal Scroll) */}
                <div className="sm:hidden divide-y divide-slate-800/80">
                  {filteredAndSortedDecks.map((deck, index) => (
                    <div
                      key={deck.id}
                      onClick={() => handleOpenDeckDetails(deck)}
                      className="p-3.5 flex items-center justify-between gap-3 hover:bg-slate-800/40 active:bg-slate-800/60 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center space-x-3 min-w-0 flex-1">
                        <span className="text-xs font-black text-slate-500 w-5 text-center shrink-0 font-mono">
                          {index === 0 ? <i className="fas fa-crown text-amber-400"></i> : `#${index + 1}`}
                        </span>

                        <div className="w-11 h-14 rounded-lg overflow-hidden bg-slate-950 border border-slate-800 shrink-0 flex items-center justify-center shadow-sm">
                          {deck.image_url ? (
                            <img src={deck.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <i className="fas fa-shield text-slate-600 text-xs"></i>
                          )}
                        </div>

                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {renderTierBadge(deck.tier)}
                          </div>
                          <p className="font-bold text-white text-xs truncate">
                            {deck.display_name || deck.name}
                          </p>
                          <div className="flex items-center gap-2 text-[10px] text-slate-400 font-mono">
                            <span>{deck.rounds_count} rounds</span>
                            <span>•</span>
                            <span>{deck.reports_count} torneios</span>
                          </div>
                        </div>
                      </div>

                      <div className="text-right shrink-0 space-y-0.5">
                        <span className="text-xs font-black text-purple-400 font-mono block tabular-nums">
                          {deck.meta_share}%
                        </span>
                        <span className="text-[9px] text-slate-500 font-bold block uppercase tracking-wider">
                          Meta Share
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop / Tablet View (sm:): Full Table */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-slate-800 bg-slate-950/60 text-[10px] font-black uppercase tracking-widest text-slate-400">
                        <th className="py-3.5 px-4 w-12 text-center">#</th>
                        <th className="py-3.5 px-4">Arquétipo / Deck</th>
                        <th className="py-3.5 px-4">Tier</th>
                        <th className="py-3.5 px-4">Cores / Atributos</th>
                        <th className="py-3.5 px-4 text-center">Rodadas</th>
                        <th className="py-3.5 px-4 text-center">Torneios</th>
                        <th className="py-3.5 px-4 text-right">Meta Share</th>
                        <th className="py-3.5 px-4 w-12 text-center">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/60 text-xs font-medium">
                      {filteredAndSortedDecks.map((deck, index) => (
                        <tr
                          key={deck.id}
                          onClick={() => handleOpenDeckDetails(deck)}
                          className="hover:bg-slate-800/50 transition-colors cursor-pointer group"
                        >
                          <td className="py-3 px-4 text-center font-bold text-slate-400">
                            {index === 0 ? (
                              <i className="fas fa-crown text-amber-400"></i>
                            ) : (
                              index + 1
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center space-x-3">
                              {deck.image_url ? (
                                <img
                                  src={deck.image_url}
                                  alt=""
                                  className="w-8 h-10 rounded-lg object-cover border border-slate-800 flex-shrink-0"
                                />
                              ) : (
                                <div className="w-8 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-slate-500 flex-shrink-0">
                                  <i className="fas fa-shield text-xs"></i>
                                </div>
                              )}
                              <div className="min-w-0">
                                <p className="font-bold text-white group-hover:text-purple-300 transition-colors truncate">
                                  {deck.display_name || deck.name}
                                </p>
                                <p className="text-[10px] text-slate-400">{deck.game_name}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            {renderTierBadge(deck.tier)}
                          </td>
                          <td className="py-3 px-4">
                            {renderColorBadges(deck.colors)}
                          </td>
                          <td className="py-3 px-4 text-center font-bold text-white font-mono tabular-nums">
                            {deck.rounds_count}
                          </td>
                          <td className="py-3 px-4 text-center text-slate-300 font-semibold font-mono tabular-nums">
                            {deck.reports_count}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <span className="font-black text-purple-400 font-mono tabular-nums">{deck.meta_share}%</span>
                              <div className="w-12 bg-slate-950 rounded-full h-1.5 overflow-hidden hidden sm:block">
                                <div
                                  className="bg-purple-500 h-full rounded-full"
                                  style={{ width: `${Math.min(100, Math.max(5, deck.meta_share))}%` }}
                                ></div>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center text-slate-500 group-hover:text-white">
                            <i className="fas fa-chevron-right text-xs"></i>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* VIEW 3: ANALYTICS & BREAKDOWN */}
            {viewMode === 'analytics' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {/* Meta Share Ranking Chart */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 sm:p-5 space-y-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-black text-white uppercase tracking-wider flex items-center space-x-2">
                      <i className="fas fa-chart-column text-purple-400"></i>
                      <span>Distribuição de Meta Share</span>
                    </h3>
                    <span className="text-[10px] text-slate-400 uppercase font-bold">Top 8 Decks</span>
                  </div>

                  <div className="space-y-3 pt-2">
                    {filteredAndSortedDecks.slice(0, 8).map((deck, idx) => (
                      <div key={deck.id} className="space-y-1">
                        <div className="flex items-center justify-between text-xs gap-2">
                          <span className="font-bold text-white truncate max-w-[130px] xs:max-w-[180px] sm:max-w-[220px] flex items-center space-x-1.5">
                            <span className="text-slate-500 text-[10px] font-mono">#{idx + 1}</span>
                            <span className="truncate">{deck.display_name || deck.name}</span>
                          </span>
                          <div className="flex items-center space-x-1.5 sm:space-x-2 shrink-0 font-mono tabular-nums">
                            <span className="text-[10px] text-slate-400">({deck.rounds_count} rds)</span>
                            <span className="font-black text-purple-400 text-xs">{deck.meta_share}%</span>
                          </div>
                        </div>
                        <div className="w-full bg-slate-950 rounded-full h-2 overflow-hidden border border-slate-800">
                          <div
                            className="bg-gradient-to-r from-purple-600 to-pink-600 h-full rounded-full transition-all duration-500"
                            style={{ width: `${Math.min(100, Math.max(5, deck.meta_share))}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Win Rate Against Opponent Meta Decks */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 sm:p-5 space-y-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-black text-white uppercase tracking-wider flex items-center space-x-2">
                      <i className="fas fa-swords text-amber-400"></i>
                      <span>Confrontos Registrados</span>
                    </h3>
                    <span className="text-[10px] text-slate-400 uppercase font-bold">Histórico Geral</span>
                  </div>

                  <div className="space-y-3 pt-2">
                    {filteredAndSortedDecks.slice(0, 8).map((deck) => {
                      const wins = deck.wins_against || 0;
                      const losses = deck.losses_against || 0;
                      const total = wins + losses;
                      const wr = total > 0 ? Math.round((wins / total) * 100) : 50;

                      return (
                        <div key={deck.id} className="space-y-1">
                          <div className="flex items-center justify-between text-xs gap-2">
                            <span className="font-bold text-white truncate max-w-[130px] xs:max-w-[180px] sm:max-w-[220px]">
                              {deck.display_name || deck.name}
                            </span>
                            <div className="flex items-center space-x-1.5 sm:space-x-2 text-[10px] shrink-0 font-mono tabular-nums">
                              <span className="text-emerald-400 font-bold">{wins}W</span>
                              <span className="text-slate-500">-</span>
                              <span className="text-rose-400 font-bold">{losses}L</span>
                              <span className="font-black text-slate-300 ml-0.5 sm:ml-1">({wr}%)</span>
                            </div>
                          </div>
                          <div className="w-full bg-slate-950 rounded-full h-2 overflow-hidden border border-slate-800 flex">
                            <div
                              className="bg-emerald-500 h-full"
                              style={{ width: `${wr}%` }}
                            ></div>
                            <div
                              className="bg-rose-500 h-full flex-1"
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

      </div>

      {/* Modal de Detalhes do Meta Deck */}
      {selectedDeckDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/85 animate-in fade-in duration-150">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[92vh] sm:max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">
            {/* Header do Modal */}
            <div className="p-3.5 sm:p-5 border-b border-slate-800 flex items-center justify-between bg-slate-950/80">
              <div className="flex items-center space-x-3 min-w-0">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-purple-600/20 border border-purple-500/30 flex items-center justify-center text-purple-400 shrink-0">
                  <i className="fas fa-shield-halved text-sm sm:text-base"></i>
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm sm:text-base font-black text-white truncate">
                    {selectedDeckDetails.display_name || selectedDeckDetails.name}
                  </h3>
                  <p className="text-[11px] sm:text-xs text-slate-400 truncate">
                    {selectedDeckDetails.game_name} • Detalhes do Arquétipo
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedDeckDetails(null)}
                className="w-8 h-8 rounded-lg bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center transition-colors cursor-pointer shrink-0 ml-2"
              >
                <i className="fas fa-xmark text-sm"></i>
              </button>
            </div>

            {/* Conteúdo do Modal com Scroll */}
            <div className="p-3.5 sm:p-6 overflow-y-auto space-y-4 sm:space-y-6 flex-1">
              {/* Card Resumo */}
              <div className="bg-slate-950 border border-slate-800 rounded-xl p-3.5 sm:p-4 flex flex-row items-center sm:items-start gap-3 sm:gap-4">
                {selectedDeckDetails.image_url ? (
                  <img
                    src={selectedDeckDetails.image_url}
                    alt=""
                    className="w-16 h-22 sm:w-20 sm:h-28 rounded-xl object-cover border border-slate-700 shadow-md shrink-0"
                  />
                ) : (
                  <div className="w-16 h-22 sm:w-20 sm:h-28 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-600 shrink-0">
                    <i className="fas fa-shield text-2xl"></i>
                  </div>
                )}

                <div className="flex-1 min-w-0 space-y-2 text-left">
                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                    {renderTierBadge(selectedDeckDetails.tier)}
                    {renderColorBadges(selectedDeckDetails.colors)}
                  </div>

                  <h4 className="text-sm sm:text-base font-black text-white truncate">
                    {selectedDeckDetails.display_name || selectedDeckDetails.name}
                  </h4>

                  <div className="grid grid-cols-3 gap-1.5 sm:gap-2 pt-1 text-center font-mono">
                    <div className="bg-slate-900 rounded-lg p-1.5 sm:p-2 border border-slate-800">
                      <p className="text-[8.5px] sm:text-[9px] uppercase font-bold text-slate-400">Meta Share</p>
                      <p className="text-xs sm:text-sm font-black text-purple-400">{selectedDeckDetails.meta_share}%</p>
                    </div>
                    <div className="bg-slate-900 rounded-lg p-1.5 sm:p-2 border border-slate-800">
                      <p className="text-[8.5px] sm:text-[9px] uppercase font-bold text-slate-400">Rodadas</p>
                      <p className="text-xs sm:text-sm font-black text-white">{selectedDeckDetails.rounds_count}</p>
                    </div>
                    <div className="bg-slate-900 rounded-lg p-1.5 sm:p-2 border border-slate-800">
                      <p className="text-[8.5px] sm:text-[9px] uppercase font-bold text-slate-400">Torneios</p>
                      <p className="text-xs sm:text-sm font-black text-white">{selectedDeckDetails.reports_count}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Histórico Recente de Confrontos */}
              <div className="space-y-2.5 sm:space-y-3">
                <h4 className="text-xs font-black text-slate-300 uppercase tracking-wider flex items-center space-x-2">
                  <i className="fas fa-history text-purple-400"></i>
                  <span>Confrontos Recentes Registrados</span>
                </h4>

                {loadingMatchups ? (
                  <div className="py-6 text-center text-xs text-slate-400">
                    <div className="animate-spin h-5 w-5 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                    Buscando confrontos...
                  </div>
                ) : deckMatchups.length === 0 ? (
                  <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 text-center text-xs text-slate-400">
                    Nenhum confronto detalhado registrado para este arquétipo no momento.
                  </div>
                ) : (
                  <div className="space-y-2 max-h-56 sm:max-h-60 overflow-y-auto">
                    {deckMatchups.map((m, idx) => (
                      <div
                        key={`matchup-${idx}`}
                        className="bg-slate-950 border border-slate-800 rounded-xl p-2.5 sm:p-3 flex items-center justify-between text-xs gap-2"
                      >
                        <div className="min-w-0 space-y-0.5 flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="font-bold text-white truncate">
                              vs {m.player_deck_name}
                            </span>
                            <span className="text-[10px] text-slate-500 font-mono shrink-0">
                              (R{m.round_number})
                            </span>
                          </div>
                          <p className="text-[10px] text-slate-400 truncate">
                            {m.tournament_name} • {m.event_category_name}
                          </p>
                        </div>

                        <div className="shrink-0">
                          {m.result === 'win' ? (
                            <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[10px] font-black uppercase">
                              Vitória
                            </span>
                          ) : m.result === 'loss' ? (
                            <span className="px-2 py-0.5 rounded bg-rose-500/20 text-rose-300 border border-rose-500/40 text-[10px] font-black uppercase">
                              Derrota
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-400 text-[10px] font-bold">
                              Empate
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Botões de Ação Rápida */}
              <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-slate-800">
                <Link
                  to={`/decks?search=${encodeURIComponent(selectedDeckDetails.name)}`}
                  className="w-full sm:flex-1 px-4 py-2.5 bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs rounded-xl text-center transition-colors flex items-center justify-center space-x-2 active:scale-95"
                >
                  <i className="fas fa-table-list"></i>
                  <span>Ver Decks da Comunidade</span>
                </Link>
                <Link
                  to={`/busca?q=${encodeURIComponent(selectedDeckDetails.name)}`}
                  className="w-full sm:flex-1 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs rounded-xl text-center transition-colors flex items-center justify-center space-x-2 active:scale-95"
                >
                  <i className="fas fa-magnifying-glass"></i>
                  <span>Buscar Cartas</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
